import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { apiErr } from "../lib/api";
import { BrandLogo } from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const dest = (role) =>
  role === "super_admin" ? "/admin"
  : (role === "operator" || role === "driver") ? "/dashboard"
  : (role === "tenant_admin" || role === "store_manager") ? "/manage"
  : role === "customer" ? "/account"
  : "/";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password, remember);
      toast.success(`Benvenuto, ${u.name}`);
      nav(dest(u.role));
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 brand-bg">
        <Link to="/"><BrandLogo name="Pickfood" color="#fff" className="text-white" /></Link>
        <div>
          <h1 className="font-head text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
            Il tuo locale,<br/>ordini senza attese.
          </h1>
          <p className="mt-4 text-white/85 text-lg max-w-md">
            La piattaforma SaaS per prenotare e vendere panini e food. Multi-sede, white-label, in tempo reale.
          </p>
        </div>
        <p className="text-white/70 text-sm">© Pickfood · www.pickfood.it</p>
      </div>

      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="md:hidden mb-8"><Link to="/"><BrandLogo /></Link></div>
          <h2 className="font-head text-2xl font-bold">Accedi</h2>
          <p className="text-muted-foreground mb-6 text-sm">Staff, titolari e amministratori Pickfood.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     required data-testid="login-email" className="h-11 mt-1" placeholder="nome@esempio.it" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     required data-testid="login-password" className="h-11 mt-1" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit"
                    className="w-full h-11 brand-bg hover:opacity-90 rounded-full text-base font-semibold">
              {busy ? <Loader2 className="animate-spin" /> : "Entra"}
            </Button>
            <label className="flex items-center gap-2 text-sm text-muted-foreground select-none cursor-pointer" data-testid="login-remember">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[hsl(var(--brand))] w-4 h-4" />
              Ricordami su questo dispositivo
            </label>
            <div className="text-center">
              <Link to="/recupera-password" className="text-sm brand-text hover:underline" data-testid="login-forgot">Password dimenticata?</Link>
            </div>
          </form>
          <div className="mt-6 text-xs text-muted-foreground bg-secondary rounded-lg p-3 space-y-1">
            <p className="font-semibold text-foreground">Demo</p>
            <p>Super admin: agenziapvr@gmail.com / Pickfood2026!</p>
            <p>Titolare: titolare@paninoteca-demo.it / Titolare2026!</p>
            <p>Operatore: operatore@paninoteca-demo.it / Operatore2026!</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
